import React, { useEffect, useMemo, useState } from "react";
import {
    Box,
    Typography,
    Paper,
    Button,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    InputAdornment,
    Pagination,
} from "@mui/material";
import { Search, CheckCircle, MailOutline } from "@mui/icons-material";
import axios from "axios";
import Cookies from "js-cookie";
import base_url from "../../constants";
import {
    buildShortDescription,
    openTemplatePreviewWindow,
} from "../../utils/dummyEmailTemplatesStore";
const ROWS_PER_PAGE = 8;

const TABLE_SURFACE_SX = {
    borderRadius: 2,
    border: "1px solid rgba(15,23,42,0.08)",
    bgcolor: "#fff",
    boxShadow: "0 4px 18px rgba(15,23,42,0.05)",
    overflow: "hidden",
};

const TABLE_HEAD_CELL_SX = {
    fontWeight: 800,
    color: "text.primary",
    bgcolor: "#f8fafc",
    borderBottom: "1px solid",
    borderColor: "divider",
    py: 1.5,
};

const truncateText = (text, maxLength) => {
    if (!text || text.length <= maxLength) return text || "";
    return `${text.slice(0, maxLength).trim()}...`;
};

const TemplatePreviewThumbnail = ({ html, title }) => {
    const previewHtml = html?.trim();

    if (!previewHtml) {
        return (
            <Box
                sx={{
                    width: 96,
                    height: 68,
                    borderRadius: 1.5,
                    bgcolor: "#f4f7fb",
                    border: "1px solid rgba(15,23,42,0.08)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "text.disabled",
                }}
            >
                <MailOutline sx={{ fontSize: 28, opacity: 0.45 }} />
            </Box>
        );
    }

    return (
        <Box
            onClick={() => openTemplatePreviewWindow(previewHtml, title || "Email Template Preview")}
            sx={{
                width: 96,
                height: 68,
                borderRadius: 1.5,
                bgcolor: "#f4f7fb",
                border: "1px solid rgba(15,23,42,0.08)",
                overflow: "hidden",
                position: "relative",
                cursor: "pointer",
                transition: "box-shadow 0.2s ease",
                "&:hover": {
                    boxShadow: "0 4px 14px rgba(15,23,42,0.12)",
                },
            }}
        >
            <Box sx={{ position: "absolute", inset: 0, overflow: "hidden" }}>
                <Box
                    sx={{
                        width: "250%",
                        height: "250%",
                        transform: "scale(0.4)",
                        transformOrigin: "top left",
                        p: 1.5,
                        pointerEvents: "none",
                        bgcolor: "#fff",
                    }}
                    dangerouslySetInnerHTML={{ __html: previewHtml }}
                />
            </Box>
        </Box>
    );
};

const SelectEmailTemplateTable = ({ onTemplateSelect }) => {
    const [templates, setTemplates] = useState([]);
    const [selectedId, setSelectedId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [query, setQuery] = useState("");
    const [page, setPage] = useState(1);

    const userRole = Cookies.get("userRole")?.split(",")[0];
    const userId = Cookies.get("userId");

    useEffect(() => {
        const fetchTemplates = async () => {
            setLoading(true);
            try {
                const response = await axios.get(`${base_url}/email-template/getbyroleanduserid`, {
                    params: { user_id: userId, role: userRole },
                });
                setTemplates(response.data.data || []);
            } catch (error) {
                console.error("Error fetching templates:", error);
                setTemplates([]);
            } finally {
                setLoading(false);
            }
        };
        fetchTemplates();
    }, [userId, userRole]);

    const filteredTemplates = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return templates;

        return templates.filter((template) => {
            const title = String(template.template_title || "").toLowerCase();
            const description = buildShortDescription(template.template || "").toLowerCase();
            return title.includes(q) || description.includes(q);
        });
    }, [query, templates]);

    const totalPages = Math.max(1, Math.ceil(filteredTemplates.length / ROWS_PER_PAGE));
    const paginatedTemplates = filteredTemplates.slice(
        (page - 1) * ROWS_PER_PAGE,
        page * ROWS_PER_PAGE
    );

    const handleSelect = (template) => {
        setSelectedId(template.id);
        onTemplateSelect({
            subject: template.template_title || "",
            content: template.template || "",
            templateName: template.template_title || "",
            templateId: template.id,
        });
    };

    if (loading) {
        return (
            <Box sx={{ py: 8, display: "flex", justifyContent: "center" }}>
                <CircularProgress size={34} />
            </Box>
        );
    }

    return (
        <Box sx={{ width: "100%", maxWidth: "100%", minWidth: 0, overflowX: "hidden" }}>
            <Paper elevation={0} sx={TABLE_SURFACE_SX}>
                <Box
                    sx={{
                        p: 2,
                        borderBottom: "1px solid",
                        borderColor: "divider",
                        bgcolor: "#fff",
                    }}
                >
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Search template name or description..."
                        value={query}
                        onChange={(event) => {
                            setQuery(event.target.value);
                            setPage(1);
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search sx={{ color: "text.secondary" }} />
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            "& .MuiOutlinedInput-root": {
                                borderRadius: 2,
                                backgroundColor: "#fff",
                            },
                        }}
                    />
                </Box>

                <TableContainer sx={{ bgcolor: "#fff" }}>
                    <Table size="small" stickyHeader sx={{ bgcolor: "#fff" }}>
                        <TableHead>
                            <TableRow sx={{ bgcolor: "#f8fafc" }}>
                                {["Name", "Short Description", "Preview", "Select"].map((heading) => (
                                    <TableCell
                                        key={heading}
                                        align={heading === "Select" ? "center" : "left"}
                                        sx={TABLE_HEAD_CELL_SX}
                                    >
                                        {heading}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>

                        <TableBody sx={{ bgcolor: "#fff" }}>
                            {paginatedTemplates.length === 0 ? (
                                <TableRow sx={{ bgcolor: "#fff" }}>
                                    <TableCell colSpan={4} align="center" sx={{ py: 5, bgcolor: "#fff" }}>
                                        <Typography variant="body2" color="text.secondary">
                                            {query ? "No templates found matching your search" : "No templates found"}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                paginatedTemplates.map((template) => {
                                    const title = template.template_title || "Untitled Template";
                                    const description = buildShortDescription(
                                        template.template,
                                        "No preview content available"
                                    );
                                    const isSelected = selectedId === template.id;

                                    return (
                                        <TableRow
                                            key={template.id}
                                            hover
                                            selected={isSelected}
                                            sx={{
                                                bgcolor: "#fff",
                                                "& td": {
                                                    borderBottom: "1px solid",
                                                    borderColor: "divider",
                                                    py: 1.8,
                                                    bgcolor: "#fff",
                                                },
                                                "&.Mui-selected": {
                                                    bgcolor: "rgba(25,118,210,0.06)",
                                                    "& td": {
                                                        bgcolor: "rgba(25,118,210,0.06)",
                                                    },
                                                },
                                                "&.Mui-selected:hover": {
                                                    bgcolor: "rgba(25,118,210,0.08)",
                                                    "& td": {
                                                        bgcolor: "rgba(25,118,210,0.08)",
                                                    },
                                                },
                                                "&:hover": {
                                                    bgcolor: "rgba(25,118,210,0.03)",
                                                    "& td": {
                                                        bgcolor: "rgba(25,118,210,0.03)",
                                                    },
                                                },
                                            }}
                                        >
                                        <TableCell>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    fontWeight: 700,
                                                    color: "text.primary",
                                                }}
                                            >
                                                {title}
                                            </Typography>
                                        </TableCell>

                                        <TableCell sx={{ maxWidth: 280 }}>
                                            <Typography variant="body2" color="text.secondary">
                                                {truncateText(description, 72)}
                                            </Typography>
                                        </TableCell>

                                        <TableCell>
                                            <TemplatePreviewThumbnail
                                                html={template.template}
                                                title={title}
                                            />
                                        </TableCell>

                                        <TableCell align="center">
                                            <Button
                                                variant={isSelected ? "contained" : "outlined"}
                                                size="small"
                                                startIcon={isSelected ? <CheckCircle sx={{ fontSize: 16 }} /> : null}
                                                onClick={() => handleSelect(template)}
                                                sx={{
                                                    textTransform: "none",
                                                    fontWeight: 700,
                                                    borderRadius: 2,
                                                    minWidth: 92,
                                                }}
                                            >
                                                {isSelected ? "Selected" : "Select"}
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
                </TableContainer>

                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 2,
                        px: 2,
                        py: 1.5,
                        flexWrap: "wrap",
                        borderTop: "1px solid",
                        borderColor: "divider",
                        bgcolor: "#fff",
                    }}
                >
                    <Typography variant="body2" color="text.secondary">
                        Showing{" "}
                        {filteredTemplates.length === 0
                            ? 0
                            : (page - 1) * ROWS_PER_PAGE + 1}
                        -
                        {Math.min(page * ROWS_PER_PAGE, filteredTemplates.length)} of{" "}
                        {filteredTemplates.length} templates
                    </Typography>

                    <Pagination
                        count={totalPages}
                        page={page}
                        onChange={(_, value) => setPage(value)}
                        color="primary"
                        shape="rounded"
                        size="small"
                    />
                </Box>
            </Paper>
        </Box>
    );
};

export default SelectEmailTemplateTable;
